import React, { useState } from "react";
import { shopsAPI } from "../services/api";
import { useI18n } from "../i18n";
import { useMembership } from "../context/MembershipContext";

/**
 * Where the shop is, for the customer menu's "nearest shop first" among an
 * owner's shops. Taken from the phone while the owner stands in the shop — no map
 * to search, nothing to type — and kept as two numbers on the shop.
 */
const ShopLocation = ({ shop, onSaved }) => {
  const { t } = useI18n();
  const { can } = useMembership();
  const canEdit = can("shop.edit");
  const [state, setState] = useState({ busy: false, error: "" });

  const save = async (latitude, longitude) => {
    try {
      const { data } = await shopsAPI.update({ latitude, longitude });
      onSaved(data.shop);
      setState({ busy: false, error: "" });
    } catch (err) {
      setState({ busy: false, error: err.response?.data?.message || err.response?.data?.error || t("shop.menuOptions.saveFailed") });
    }
  };

  const locate = () => {
    if (!navigator.geolocation) {
      setState({ busy: false, error: t("shops.sister.unavailable") });
      return;
    }

    setState({ busy: true, error: "" });
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => save(coords.latitude, coords.longitude),
      () => setState({ busy: false, error: t("shops.sister.denied") }),
      { enableHighAccuracy: true, timeout: 20000 },
    );
  };

  const located = shop.latitude !== null && shop.latitude !== undefined;

  return (
    <div className="bg-white rounded-xl shadow-lg p-6">
      <h2 className="text-lg font-semibold text-gray-800 mb-1">{t("shops.sister.location")}</h2>
      <p className="text-sm text-gray-500 mb-4">{t("shops.sister.locationHint")}</p>

      <p className="text-sm text-gray-800 mb-3" dir="ltr">
        {located
          ? t("shops.sister.located", {
              lat: Number(shop.latitude).toFixed(5),
              lng: Number(shop.longitude).toFixed(5),
            })
          : t("shops.sister.none")}
      </p>

      {state.error && <p className="mb-3 text-sm text-red-700">{state.error}</p>}

      {canEdit && (
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={locate}
            disabled={state.busy}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
          >
            📍 {state.busy ? t("shops.sister.locating") : t("shops.sister.useHere")}
          </button>
          {located && (
            <button
              type="button"
              onClick={() => save(null, null)}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              {t("shops.sister.clear")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ShopLocation;
